import { lazy, Suspense, useEffect, useState } from "react"

const HeroOrb = lazy(() =>
  import("./HeroOrb").then((m) => ({ default: m.HeroOrb })),
)

const FALLBACK_DOT_STYLE: React.CSSProperties = {
  backgroundImage:
    "radial-gradient(circle at 30% 30%, color-mix(in srgb, var(--mesa-primary) 8%, transparent) 1px, transparent 1.5px)",
  backgroundSize: "14px 14px",
  maskImage: "radial-gradient(circle at center, #000 55%, transparent 72%)",
  WebkitMaskImage:
    "radial-gradient(circle at center, #000 55%, transparent 72%)",
}

function OrbFallback() {
  return (
    <div
      aria-hidden="true"
      className="relative mx-auto aspect-square w-full max-w-sm sm:max-w-md lg:max-w-md"
    >
      <div className="absolute inset-0 rounded-full bg-mesa-bg-subtle shadow-[inset_0_-20px_60px_color-mix(in_srgb,var(--mesa-primary)_6%,transparent)]" />
      <div
        className="absolute inset-[8%] rounded-full opacity-40"
        style={FALLBACK_DOT_STYLE}
      />
    </div>
  )
}

type IdleCallbackWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  cancelIdleCallback?: (id: number) => void
}

const IDLE_TIMEOUT_MS = 2500

export function DeferredHeroOrb() {
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined
    }

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    if (mq.matches) {
      return undefined
    }

    const w = window as IdleCallbackWindow
    const schedule =
      w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1))

    const id = schedule(() => setShouldRender(true), {
      timeout: IDLE_TIMEOUT_MS,
    })

    return () => {
      const cancel = w.cancelIdleCallback ?? window.clearTimeout
      cancel(id as number)
    }
  }, [])

  if (!shouldRender) {
    return <OrbFallback />
  }

  return (
    <Suspense fallback={<OrbFallback />}>
      <HeroOrb />
    </Suspense>
  )
}
